import React, { useCallback, useMemo } from "react";

import { Grid } from "@material-ui/core";
import { useSelector } from "react-redux";

import {
  Input,
  SelectField,
  TextArea,
  withNamespace,
} from "components/Form/FormikControls";

import { groupedLineItemToolTipText } from "constants/billing";
import { ValueSource } from "constants/ruleBooks";

import { FieldValueType } from "containers/Settings/RuleBooks/constants";
import {
  compareDynamicFieldOption,
  mapOptionToConstantFieldOption,
} from "containers/Settings/RuleBooks/lib";
import {
  RuleOutputFormatField,
  getOutputFormatOptions,
} from "containers/Settings/RuleBooks/Rules/CustomRules/PresentationFormatting/RuleOutputFormatField";
import { getDataTypeByPath } from "containers/Settings/RuleBooks/Schema/schema";
import { useRuleFieldSchema } from "containers/Settings/RuleBooks/Schema/schemaContext";

import { EMPTY_OBJECT } from "lib";

import {
  selectCustomisationsByRuleIdLookup,
  selectLedgerAccountOptionsForRuleBuilder,
} from "selectors";

import { useFieldValue } from "hooks";

export function CustomiseManagedRuleForm(props) {
  const { namespace: ns, inputPath, ruleId } = props;

  const schema = useRuleFieldSchema();

  const LedgerAccountOptions = useSelector(
    selectLedgerAccountOptionsForRuleBuilder,
  );

  const value = useFieldValue(ns);

  const unitAmountOutputFormatOptions = getOutputFormatOptions(
    value,
    "value.amount.valueType",
    "unit_amount_raw_format",
  );
  const quantityOutputFormatOptions = getOutputFormatOptions(
    value,
    "value.units.valueType",
    "quantity_raw_format",
  );
  const dataType = getDataTypeByPath(schema, inputPath);

  let glCodeProperty = null;
  if (dataType) {
    // Auto GL Code can be used when the "rootFieldId" in the schema has a `gl_code` type property.
    glCodeProperty = dataType.properties.find(
      schemaField => schemaField.type === FieldValueType.GL_CODE,
    );
  }

  const automaticGlCodeOptions = useMemo(
    () =>
      glCodeProperty
        ? [
            {
              label: "Automatically determined",
              value: {
                source: ValueSource.FIELD,
                value: glCodeProperty.via || glCodeProperty.id,
              },
            },
          ]
        : [],
    [glCodeProperty],
  );

  const allLedgerAccountOptions = useMemo(
    () =>
      automaticGlCodeOptions.concat(
        LedgerAccountOptions.map(mapOptionToConstantFieldOption),
      ),
    [automaticGlCodeOptions, LedgerAccountOptions],
  );

  const customisedFields =
    useSelector(selectCustomisationsByRuleIdLookup)[ruleId] || EMPTY_OBJECT;

  const getAltToolTip = useCallback(
    field => {
      if (value?.[field] && customisedFields?.[field]) {
        return `Overrides default value "${customisedFields[field]}"`;
      }
    },
    [customisedFields, value],
  );

  return (
    <>
      <Grid item xs={12}>
        <Input
          name={withNamespace(ns, "name")}
          label="Name"
          placeholder={customisedFields.name}
          altTooltip={getAltToolTip("name")}
        />
      </Grid>
      <Grid item xs={12}>
        <TextArea
          name={withNamespace(ns, "comment")}
          label="Comment"
          placeholder={customisedFields.comment}
          altTooltip={getAltToolTip("comment")}
        />
      </Grid>

      <Grid item xs={12}>
        <TextArea
          name={withNamespace(ns, "title_template")}
          label="Title Template"
          altTooltip={getAltToolTip("title_template")}
        />
      </Grid>

      <Grid item xs={12}>
        <TextArea
          name={withNamespace(ns, "invoice_line_item_template")}
          label="Document Grouped Line Description"
          tooltip={groupedLineItemToolTipText}
          altTooltip={getAltToolTip("invoice_line_item_template")}
        />
      </Grid>

      <Grid item xs={12}>
        <Input
          name={withNamespace(ns, "separate_document_key")}
          label="Separate Document Key"
          tooltip="An optional common value allowing regrouping of outputted ledger entries into separate documents.  Each unique value (including blanks) will group ledger entries with that value into a single document."
          altTooltip={getAltToolTip("separate_document_key")}
        />
      </Grid>

      <Grid item xs={12}>
        <SelectField
          altTooltip={getAltToolTip("gl_code")}
          comparer={compareDynamicFieldOption}
          data-tour="glCode"
          label="GL Code"
          menuPortalTarget={document.body}
          name={withNamespace(ns, "gl_code")}
          options={allLedgerAccountOptions}
          required
        />
      </Grid>

      <Grid item xs={6}>
        <RuleOutputFormatField
          altTooltip={getAltToolTip("quantity_output_format")}
          label="Units Format"
          name="quantity_output_format"
          options={quantityOutputFormatOptions}
        />
      </Grid>
      <Grid item xs={6}>
        <RuleOutputFormatField
          altTooltip={getAltToolTip("unit_amount_output_format")}
          label="Unit Price Format"
          name="unit_amount_output_format"
          options={unitAmountOutputFormatOptions}
        />
      </Grid>
    </>
  );
}
